import type { PersistentAgentCompactionEntry } from '@cortex/types';
import type { TimelineCompaction } from '@formant/chat';

/**
 * Compaction history as it arrives over HTTP. Identical to
 * `PersistentAgentCompactionEntry` except for `createdAt`: the Zod contract types
 * it as a `Date` (it is one in Postgres and on the server), but JSON has no date
 * type, so what actually lands here is the ISO string. Stated explicitly rather
 * than cast away, because the timeline positions markers by parsing it.
 */
export type CompactionWireEntry = Omit<PersistentAgentCompactionEntry, 'createdAt'> & {
  createdAt: string;
};

/**
 * Map the API's compaction rows to timeline markers. Shared by the thread detail
 * page and the orchestration chat panel so both render compaction identically —
 * the same reason the reset confirm copy is shared (see -thread-lifecycle).
 *
 * Rows with an unparseable `createdAt` are dropped: `buildTimeline` positions a
 * marker by its timestamp, and one that cannot be placed would silently land at
 * the end of the conversation, claiming the agent's context was replaced at a
 * point where it was not.
 */
export function toTimelineCompactions(
  items: readonly CompactionWireEntry[] | undefined,
): TimelineCompaction[] {
  if (!items) return [];
  return items
    .filter((c) => !Number.isNaN(Date.parse(c.createdAt)))
    .map((c) => ({
      version: c.version,
      msgCount: c.msgCount,
      summarizedCount: c.summarizedCount,
      forced: c.forced,
      summary: c.summary,
      at: c.createdAt,
    }));
}
